import {
  Autocomplete,
  Box,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextLabel from "../../components/TextLabel";
import { locales } from "../../localization";
import StyledTextField from "../../styledComponents/styledTextField";
import { useGlobalStateContext } from "../../context/globalState";
import StyledButtons from "../../styledComponents/styledButtons";
import StyledDialog from "../../styledComponents/styledDialog";
import axios from "axios";
import { useGetToken } from "../../hooks/useGetToken";
import Context from "../../context";
import Loading from "../Loading";

interface AdminSettingPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSettingPage: React.FC<AdminSettingPageProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    userPreference: { languagePreference },
    setUserPreference,
    loading,
    beginLoading,
    endLoading,
  } = useGlobalStateContext();
  const {
    authState: { user },
  } = useContext(Context);
  const [getToken] = useGetToken();
  const { t, i18n } = useTranslation();

  const [selectedOption, setSelectedOption] =
    useState<string>(languagePreference);

  useEffect(() => {
    if (languagePreference) {
      i18n.changeLanguage(languagePreference || "en-US");

      if (languagePreference !== selectedOption) {
        setSelectedOption(languagePreference);
      }
    }
  }, [languagePreference]);

  const handleLanguageChange = (newValue: string) => {
    setSelectedOption(newValue);
    i18n.changeLanguage(newValue); // Change the language in i18n
  };

  const onCloseDialog = () => {
    i18n.changeLanguage(languagePreference);
    onClose();
  };

  const onSave = async () => {
    const loadingToken = beginLoading("admin-setting-save");
    setUserPreference((prevState) => ({
      ...prevState,
      languagePreference: selectedOption,
    }));
    try {
      const token = await getToken(); // Get the token from your custom hook
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token, // Include the token in the header if required
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/userSetting/addPreference`,
        { language: selectedOption, id: user?.username },
        config,
      );

      if (response.status !== 200) {
        throw new Error("Failed to save user preference");
      }
    } catch (error) {
      console.error("Error saving user preference:", error);
    } finally {
      endLoading(loadingToken);
    }

    onClose();
  };

  if (!isOpen) return null;

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      fullWidth // Make the dialog occupy full width of its container
      maxWidth="md" // Set max width to 'md' or any other desired breakpoint (sm, md, lg, xl)
      sx={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }} // Optional: Set a minimum width if needed
    >
      {loading && <Loading />}
      <DialogTitle sx={{ paddingBottom: "6px", marginLeft: "10px" }}>
        <TextLabel type="tabHeader">{t("settings.title") as string}</TextLabel>
      </DialogTitle>
      <DialogContent>
        <FormControl
          fullWidth
          sx={{ marginTop: 2, borderRadius: 1, padding: 2 }}
        >
          <Autocomplete
            options={locales}
            getOptionLabel={(option) => option.name}
            value={
              locales.find((locale) => locale.tag === selectedOption) || null
            }
            onChange={(_, newValue) => {
              newValue && handleLanguageChange(newValue.tag);
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={t("settings.language") as string}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </FormControl>

        <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item xs={5}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center", // Center the buttons horizontally
                gap: 2, // Add spacing between the buttons
                mt: 2, // Add margin-top
              }}
            >
              <StyledButtons.SecondaryButton onClick={onCloseDialog}>
                {t("adminDashboard.form.button.cancel")}
              </StyledButtons.SecondaryButton>
              <StyledButtons.PrimaryButton type="submit" onClick={onSave}>
                {String(t("adminDashboard.form.button.save"))}
              </StyledButtons.PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  );
};

export default AdminSettingPage;
